import React, { useEffect, useState } from 'react';
import {
  Button,
  Empty,
  Input,
  Popconfirm,
  Space,
  Tag,
  Typography,
} from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import MaterialAutocomplete from './MaterialAutocomplete';
import MaterialCreationModal from './modals/MaterialCreationModal';

const { Text } = Typography;
const { TextArea } = Input;

const getMaterialId = (material) =>
  material?.material_id ?? material?.id ?? material?.materialId;

const ProposalItemPieceMaterialSelector = ({
  materials = [],
  onMaterialsChange,
}) => {
  const [selectedMaterials, setSelectedMaterials] = useState(materials);
  const [showMaterialModal, setShowMaterialModal] = useState(false);
  const [noteOpenIds, setNoteOpenIds] = useState(() => new Set());

  useEffect(() => {
    setSelectedMaterials(materials);
  }, [materials]);

  const commitMaterials = (next) => {
    setSelectedMaterials(next);
    onMaterialsChange(next);
  };

  const handleMaterialSelect = (material) => {
    const materialId = getMaterialId(material);
    if (
      selectedMaterials.some(
        (m) => String(m.material_id) === String(materialId)
      )
    ) {
      return;
    }

    commitMaterials([
      ...selectedMaterials,
      {
        material_id: materialId,
        name: material.name,
        code: material.code,
        color: material.color,
        type: material.type,
        style: material.style,
        description: material.description,
        material_image_path:
          material.material_image_path || material.image_path || '',
        custom_description: '',
      },
    ]);
  };

  const handleRemoveMaterial = (materialId) => {
    commitMaterials(
      selectedMaterials.filter(
        (m) => String(m.material_id) !== String(materialId)
      )
    );
    setNoteOpenIds((prev) => {
      const next = new Set(prev);
      next.delete(String(materialId));
      return next;
    });
  };

  const handleMaterialCreated = (newMaterial) => {
    handleMaterialSelect(newMaterial);
    setShowMaterialModal(false);
  };

  const updateCustomDescription = (index, value) => {
    const updated = selectedMaterials.map((material, i) =>
      i === index ? { ...material, custom_description: value } : material
    );
    commitMaterials(updated);
  };

  const toggleNote = (materialId) => {
    const key = String(materialId);
    setNoteOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const isEmpty = selectedMaterials.length === 0;

  return (
    <>
      <div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 8,
            gap: 8,
            flexWrap: 'wrap',
          }}
        >
          <Text strong style={{ fontSize: 13 }}>
            Materials
            {!isEmpty ? (
              <Text type="secondary" style={{ fontWeight: 400 }}>
                {' '}
                ({selectedMaterials.length})
              </Text>
            ) : null}
          </Text>
        </div>

        {isEmpty ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            styles={{ image: { height: 40 } }}
            description={
              <Space direction="vertical" size={2}>
                <Text style={{ fontSize: 13 }}>No materials yet</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Optional — add fabrics, finishes, etc.
                </Text>
              </Space>
            }
          >
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setShowMaterialModal(true)}
                block
              >
                Add material
              </Button>
              <MaterialAutocomplete
                onMaterialSelect={handleMaterialSelect}
                placeholder="Or search existing materials…"
                excludeSelected={selectedMaterials.map((m) => m.material_id)}
              />
            </Space>
          </Empty>
        ) : (
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <div
              style={{
                display: 'flex',
                gap: 8,
                width: '100%',
                alignItems: 'flex-start',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <MaterialAutocomplete
                  onMaterialSelect={handleMaterialSelect}
                  placeholder="Search to add another…"
                  excludeSelected={selectedMaterials.map((m) => m.material_id)}
                />
              </div>
              <Button
                icon={<PlusOutlined />}
                onClick={() => setShowMaterialModal(true)}
              >
                New
              </Button>
            </div>

            {selectedMaterials.map((material, index) => {
              const mid = String(material.material_id);
              const noteOpen =
                noteOpenIds.has(mid) || Boolean(material.custom_description);
              return (
                <div
                  key={mid || index}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 8,
                    alignItems: 'flex-start',
                    padding: '8px 10px',
                    border: '1px solid #f0f0f0',
                    borderRadius: 6,
                    background: '#fafafa',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Text strong>{material.name}</Text>
                    {(material.code || material.color) && (
                      <div style={{ marginTop: 2 }}>
                        <Space size={4} wrap>
                          {material.code ? <Tag>{material.code}</Tag> : null}
                          {material.color ? (
                            <Tag color="blue">{material.color}</Tag>
                          ) : null}
                        </Space>
                      </div>
                    )}

                    {noteOpen ? (
                      <div style={{ marginTop: 6 }}>
                        <TextArea
                          rows={2}
                          value={material.custom_description || ''}
                          onChange={(e) =>
                            updateCustomDescription(index, e.target.value)
                          }
                          placeholder="Optional note for this piece…"
                          maxLength={500}
                        />
                      </div>
                    ) : (
                      <Button
                        type="link"
                        size="small"
                        style={{ paddingInline: 0, height: 'auto' }}
                        onClick={() => toggleNote(material.material_id)}
                      >
                        Add note
                      </Button>
                    )}
                  </div>

                  <Popconfirm
                    title="Remove this material?"
                    okText="Remove"
                    cancelText="Cancel"
                    okButtonProps={{ danger: true }}
                    onConfirm={() =>
                      handleRemoveMaterial(material.material_id)
                    }
                  >
                    <Button
                      type="text"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      aria-label={`Remove ${material.name}`}
                    />
                  </Popconfirm>
                </div>
              );
            })}
          </Space>
        )}
      </div>

      <MaterialCreationModal
        show={showMaterialModal}
        onHide={() => setShowMaterialModal(false)}
        onMaterialCreated={handleMaterialCreated}
        simple
      />
    </>
  );
};

export default ProposalItemPieceMaterialSelector;
